import styled from "@emotion/styled";
import { HTMLAttributes } from "react";
import { Button, Popover, Typography } from "@kaltura/ds-react-components";
import { PopoverOrigin } from "@mui/material";
import { translate } from "@kaltura/mediaspace-shared-utils";

export interface ConfirmationPopoverProps extends HTMLAttributes<HTMLDialogElement> {
    /**
     * Is the popover currently visible
     */
    open: boolean;

    /**
     * Callback to trigger when the popover is closed (cancel or outside click)
     */
    onClose: () => void;

    /**
     * Callback to trigger when the confirm button is clicked
     */
    onConfirm: () => void;

    /**
     * Element to which the popover should be anchored
     */
    anchorEl: HTMLElement | null;

    /**
     * Confirmation message to display
     */
    message: string;

    /**
     * Label for the confirm button
     * @default "Delete"
     */
    confirmLabel?: string;

    /**
     * Label for the cancel button
     * @default "Cancel"
     */
    cancelLabel?: string;

    /**
     * Whether the confirm button should use destructive styling (red)
     * @default true
     */
    destructive?: boolean;

    /**
     * Position of the popover relative to the anchor element
     * @default {{ vertical: "bottom", horizontal: "left" }}
     */
    anchorOrigin?: PopoverOrigin;

    /**
     * Transform origin for the popover positioning
     * @default {{ vertical: "top", horizontal: "right" }}
     */
    transformOrigin?: PopoverOrigin;
}

const StyledPopoverContent = styled.div(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    paddingTop: theme.spacing(1),
    width: 220,
}));

const MessageText = styled(Typography)({
    textAlign: "center",
});

const ActionsContainer = styled.div(({ theme }) => ({
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: theme.spacing(1),
}));

/**
 * ConfirmationPopover displays a confirmation dialog anchored to a given element.
 * It shows a message and provides confirm/cancel actions.
 */
export function ConfirmationPopover(props: ConfirmationPopoverProps) {
    const {
        open,
        onClose,
        onConfirm,
        anchorEl,
        message,
        confirmLabel = translate("Delete"),
        cancelLabel = translate("Cancel"),
        destructive = true,
        anchorOrigin = { vertical: "bottom", horizontal: "left" },
        transformOrigin = { vertical: "top", horizontal: "right" },
        id,
    } = props;

    const getBoundingClientRect = () => {
        const bounds = anchorEl?.getBoundingClientRect();
        return bounds
            ? DOMRect.fromRect({ height: bounds.height, width: bounds.width, x: bounds.x, y: bounds.y })
            : new DOMRect();
    };

    const handleConfirm = () => {
        onConfirm();
        onClose();
    };

    return (
        <Popover
            id={id}
            open={open}
            anchorEl={{ getBoundingClientRect, nodeType: Node.ELEMENT_NODE }}
            onClose={onClose}
            anchorOrigin={anchorOrigin}
            transformOrigin={transformOrigin}
            className={"kms-ds-confirmation-popover"}
        >
            <StyledPopoverContent>
                <MessageText variant="body2Highlight">{message}</MessageText>
                <ActionsContainer>
                    <Button color="secondary" variant="borderless" onClick={onClose}>
                        {cancelLabel}
                    </Button>
                    <Button color={destructive ? "danger" : "primary"} onClick={handleConfirm}>
                        {confirmLabel}
                    </Button>
                </ActionsContainer>
            </StyledPopoverContent>
        </Popover>
    );
}

export default ConfirmationPopover;
